// A GTFS train's card: its whole trip, stations passed, the next one, the
// rest. Call times distinguish operator forecasts, estimates and timetables,
// independently of how the vehicle is placed. Used by the city page
// (page.ts) and the world map (apps/world), whose ids carry a prefix: `id`
// maps the trip's station ids to the train's.

import type { Network } from '@tol/core/types';
import type { CardTrain, TripRow } from '@tol/ui/card';
import { tripStops } from './trains.ts';
import type { ShapePatterns, TripStop } from './trains.ts';
import type { GtfsTrip } from './types.ts';

export function gtfsTripRows(
  t: CardTrain,
  net: Network,
  trip: GtfsTrip | undefined,
  shapes: ShapePatterns,
  id: (sid: string) => string = (sid) => sid,
): readonly TripRow[] {
  const p = t.pred;
  const pos = t.motion.pos;
  const all: readonly TripStop[] = trip
    ? tripStops(net, trip, shapes).map((s) => Object.assign({}, s, { sid: id(s.sid) }))
    : p.stops.map((s) => ({ sid: s.sid, t: s.eta }));
  const stops = p.stops;
  // Where the remaining calls start in the full trip by visit. stops is the
  // suffix of the trip from its next call on so its length marks which visit
  // is next. Fall back to station plus ETA when the suffix does not line up.
  let predAt = -1;
  if (stops.length <= all.length) {
    const cand = all.length - stops.length;
    let ok = true;
    for (let k = 0; k < stops.length; k++) {
      const a = all[cand + k];
      const s = stops[k];
      if (a === undefined || s === undefined || a.sid !== s.sid) {
        ok = false;
        break;
      }
    }
    if (ok) predAt = cand;
  }
  if (predAt < 0) {
    const want = stops[0]?.eta ?? p.eta;
    let found = all.findIndex((s) => s.sid === p.N && s.t === want);
    if (found < 0) found = all.findIndex((s) => s.sid === p.N);
    predAt = Math.max(0, found);
  }
  const next = pos?.N ?? p.N;
  let nextAt: number;
  if (next === p.N && predAt >= 0 && predAt < all.length && all[predAt]?.sid === next) {
    nextAt = predAt;
  } else {
    const cands: number[] = [];
    for (let i = 0; i < all.length; i++) {
      if (all[i]?.sid === next) cands.push(i);
    }
    if (cands.length === 0) {
      nextAt = 0;
    } else if (predAt < 0) {
      nextAt = cands[0] ?? 0;
    } else {
      let best: number | undefined;
      for (const c of cands) {
        if (c <= predAt && (best === undefined || c > best)) best = c;
      }
      if (best === undefined) {
        for (const c of cands) {
          if (c > predAt && (best === undefined || c < best)) best = c;
        }
      }
      nextAt = best ?? cands[0] ?? 0;
    }
  }
  // GPS placement doesn't make timetable-based estimates operator forecasts.
  // A supplied live trip's forecasts stay predictions even before departure.
  const source =
    p.scheduled === true
      ? 'scheduled'
      : (trip?.callTimeSource ??
        (trip?.gpsOnly === true ? 'estimated' : trip === undefined && p.waiting ? 'scheduled' : 'predicted'));
  return all.map((s, i) => {
    if (s.via) return { sid: s.sid, kind: 'via', time: s.t, source: 'none' };
    let time = s.t;
    if (predAt >= 0 && i >= predAt) {
      const st = stops[i - predAt];
      if (st !== undefined && st.sid === s.sid) time = st.eta;
    }
    return {
      sid: s.sid,
      kind: i < nextAt ? 'past' : i === nextAt ? 'next' : 'ahead',
      time,
      source: time === 0 ? 'none' : source,
    };
  });
}

// The card's note on a train: its number, and how late it is when the feed
// says ("Train 4521. 3 min late"; within a minute is on time). A scheduled
// train has no delay to give (the card says it's scheduled).
export function gtfsTrainNote(t: CardTrain, trip: GtfsTrip | undefined): string {
  const parts: string[] = [];
  if (t.pred.vid !== '') parts.push(`${trip?.line.startsWith('bus:') === true ? 'Bus' : 'Train'} ${t.pred.vid}`);
  const late = trip?.late;
  if (late !== undefined && t.pred.scheduled !== true) {
    const min = Math.round(Math.abs(late) / 60);
    parts.push(min < 1 ? 'On time' : `${min} min ${late > 0 ? 'late' : 'early'}`);
  }
  return parts.join('. ');
}
